import { Colors } from "@/enums/Colors";
import { LazoUser } from "@/models/LazoUser";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, SwipeableDrawer, Typography, styled } from "@mui/material";
import { noop } from "@tanstack/react-query";
import { Dispatch, SetStateAction } from "react";
import LazoAIChatDrawerContent from "./LazoAIChatDrawerContent";

interface LazoAIChatMobileModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  lazoUser: LazoUser;
}

export default function LazoAIChatMobileModal({ open, setOpen, lazoUser }: LazoAIChatMobileModalProps) {
  const handleClose = () => setOpen(false);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      onOpen={noop}
      disableSwipeToOpen={true}
      swipeAreaWidth={20}
      hysteresis={0.6}
      minFlingVelocity={400}
      PaperProps={{
        elevation: 24,
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          height: "95vh",
          maxHeight: "95vh",
          background: Colors.WHITE,
          width: "100vw",
          maxWidth: "100vw",
          left: "0",
          right: "0",
          margin: "0",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{ keepMounted: false }}
      transitionDuration={225}
    >
      <MobileContainer>
        <DrawerHandle
          onClick={handleClose}
          role="button"
          aria-label="Close chat"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClose();
            }
          }}
        />
        <MobileHeader>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: Colors.ELEGANT_BLACK }}>Lazo AI Assistant</Typography>
          <CloseButton onClick={handleClose} aria-label="Close chat" size="medium">
            <CloseIcon />
          </CloseButton>
        </MobileHeader>
        <MobileContentWrapper>
          <LazoAIChatDrawerContent setIsOpenDrawer={setOpen} lazoUser={lazoUser} />
        </MobileContentWrapper>
      </MobileContainer>
    </SwipeableDrawer>
  );
}

const MobileContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  overflow: "hidden",
  position: "relative",
});

const DrawerHandle = styled(Box)({
  width: "40px",
  height: "4px",
  backgroundColor: Colors.LIGHT_GRAY_DEDEE1 || "#e0e0e0",
  borderRadius: "2px",
  margin: "8px auto 0",
  cursor: "pointer",
  transition: "all 0.2s ease",
  flexShrink: 0,

  "&:hover": {
    backgroundColor: Colors.DARKER_DARKER_GRAY || "#666",
    width: "48px",
  },

  "&:active": {
    backgroundColor: Colors.LAZO_PURPLE,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE}`,
    outlineOffset: "2px",
  },
});

const MobileHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 20px 16px",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1 || "#e0e0e0"}`,
  minHeight: "56px",
  backgroundColor: Colors.WHITE,
  position: "sticky",
  top: 0,
  zIndex: 1,
  flexShrink: 0,
});

const CloseButton = styled(IconButton)({
  color: Colors.DARKER_DARKER_GRAY || "#666",
  padding: "8px",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    color: Colors.ELEGANT_BLACK,
  },

  "&:focus-visible": {
    outline: `2px solid ${Colors.LAZO_PURPLE}`,
    outlineOffset: "2px",
  },
});

const MobileContentWrapper = styled(Box)({
  flex: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  position: "relative",
});
